/**
 * Event Offers Service
 * Handles event-offer junction CRUD operations
 * Links offers directly to events with event-specific overrides
 */

import { supabase } from "./supabase";
import type { UUID } from "../types/common.types";
import type {
  EventOffer,
  EventOfferWithDetails,
  CreateEventOfferDTO,
  UpdateEventOfferDTO,
} from "../types/program.types";

// =====================================================
// EVENT OFFERS CRUD OPERATIONS
// =====================================================

/**
 * Get all offers attached to an event
 */
export async function getEventOffers(eventId: UUID): Promise<EventOffer[]> {
  const { data, error } = await supabase
    .from("event_offers")
    .select(
      `
      *,
      offer:offers(*)
    `,
    )
    .eq("event_id", eventId)
    .eq("is_active", true)
    .order("display_order", { ascending: true });

  if (error) {
    console.error("Error fetching event offers:", error);
    throw error;
  }

  return data || [];
}

/**
 * Get offers for an event with full details using the helper function
 * This applies display overrides and filters by validity dates
 */
export async function getEventOffersWithDetails(
  eventId: UUID,
): Promise<EventOfferWithDetails[]> {
  const { data, error } = await supabase.rpc("get_event_offers", {
    p_event_id: eventId,
  });

  if (error) {
    console.error("Error fetching event offers with details:", error);
    throw error;
  }

  return data || [];
}

/**
 * Get a single event-offer junction by ID
 */
export async function getEventOffer(
  eventOfferId: UUID,
): Promise<EventOffer | null> {
  const { data, error } = await supabase
    .from("event_offers")
    .select(
      `
      *,
      offer:offers(*)
    `,
    )
    .eq("id", eventOfferId)
    .single();

  if (error) {
    if (error.code === "PGRST116") {
      // Not found
      return null;
    }
    console.error("Error fetching event offer:", error);
    throw error;
  }

  return data;
}

/**
 * Attach an offer to an event
 */
export async function attachOfferToEvent(
  input: CreateEventOfferDTO,
  userId?: UUID,
): Promise<EventOffer> {
  const { data, error } = await supabase
    .from("event_offers")
    .insert({
      event_id: input.event_id,
      offer_id: input.offer_id,
      display_name: input.display_name || null,
      display_description: input.display_description || null,
      display_order: input.display_order ?? 0,
      is_featured: input.is_featured ?? false,
      cta_type: input.cta_type || "qr_code",
      base_url: input.base_url || null,
      parameters: input.parameters || {},
      created_by: userId || null,
    })
    .select(
      `
      *,
      offer:offers(*)
    `,
    )
    .single();

  if (error) {
    console.error("Error attaching offer to event:", error);
    throw error;
  }

  return data;
}

/**
 * Attach an offer to an event using the helper function
 * This handles upsert logic
 */
export async function attachOfferToEventRPC(
  eventId: UUID,
  offerId: UUID,
  options?: {
    displayName?: string;
    isFeatured?: boolean;
    displayOrder?: number;
    ctaType?: string;
    baseUrl?: string;
    parameters?: Record<string, unknown>;
  },
  userId?: UUID,
): Promise<UUID> {
  const { data, error } = await supabase.rpc("attach_offer_to_event", {
    p_event_id: eventId,
    p_offer_id: offerId,
    p_display_name: options?.displayName || null,
    p_is_featured: options?.isFeatured ?? false,
    p_display_order: options?.displayOrder ?? 0,
    p_cta_type: options?.ctaType || "qr_code",
    p_base_url: options?.baseUrl || null,
    p_parameters: options?.parameters || {},
    p_created_by: userId || null,
  });

  if (error) {
    console.error("Error attaching offer to event:", error);
    throw error;
  }

  return data;
}

/**
 * Update an event-offer junction
 */
export async function updateEventOffer(
  eventOfferId: UUID,
  updates: UpdateEventOfferDTO,
): Promise<EventOffer> {
  const { data, error } = await supabase
    .from("event_offers")
    .update({
      ...(updates.display_name !== undefined && {
        display_name: updates.display_name,
      }),
      ...(updates.display_description !== undefined && {
        display_description: updates.display_description,
      }),
      ...(updates.display_order !== undefined && {
        display_order: updates.display_order,
      }),
      ...(updates.is_featured !== undefined && {
        is_featured: updates.is_featured,
      }),
      ...(updates.cta_type !== undefined && { cta_type: updates.cta_type }),
      ...(updates.base_url !== undefined && { base_url: updates.base_url }),
      ...(updates.parameters !== undefined && {
        parameters: updates.parameters,
      }),
      ...(updates.qr_code_url !== undefined && {
        qr_code_url: updates.qr_code_url,
      }),
      ...(updates.generated_url !== undefined && {
        generated_url: updates.generated_url,
      }),
      ...(updates.is_active !== undefined && { is_active: updates.is_active }),
      ...(updates.auto_issue_on_register !== undefined && {
        auto_issue_on_register: updates.auto_issue_on_register,
      }),
      ...(updates.auto_issue_on_attendance !== undefined && {
        auto_issue_on_attendance: updates.auto_issue_on_attendance,
      }),
      ...(updates.reward_email_template_id !== undefined && {
        reward_email_template_id: updates.reward_email_template_id,
      }),
      ...(updates.issue_as_ticket !== undefined && {
        issue_as_ticket: updates.issue_as_ticket,
      }),
      updated_at: new Date().toISOString(),
    })
    .eq("id", eventOfferId)
    .select(
      `
      *,
      offer:offers(*)
    `,
    )
    .single();

  if (error) {
    console.error("Error updating event offer:", error);
    throw error;
  }

  return data;
}

/**
 * Remove an offer from an event (soft delete by setting is_active = false)
 */
export async function removeOfferFromEvent(eventOfferId: UUID): Promise<void> {
  const { error } = await supabase
    .from("event_offers")
    .update({ is_active: false, updated_at: new Date().toISOString() })
    .eq("id", eventOfferId);

  if (error) {
    console.error("Error removing offer from event:", error);
    throw error;
  }
}

/**
 * Hard delete an event-offer junction
 */
export async function deleteEventOffer(eventOfferId: UUID): Promise<void> {
  const { error } = await supabase
    .from("event_offers")
    .delete()
    .eq("id", eventOfferId);

  if (error) {
    console.error("Error deleting event offer:", error);
    throw error;
  }
}

/**
 * Reorder offers for an event
 */
export async function reorderEventOffers(
  eventId: UUID,
  orderedIds: UUID[],
): Promise<void> {
  // Update display_order for each offer
  const updates = orderedIds.map((id, index) =>
    supabase
      .from("event_offers")
      .update({ display_order: index, updated_at: new Date().toISOString() })
      .eq("id", id)
      .eq("event_id", eventId),
  );

  const results = await Promise.all(updates);
  const errors = results.filter((r) => r.error);

  if (errors.length > 0) {
    console.error("Errors reordering event offers:", errors);
    throw new Error("Failed to reorder some offers");
  }
}

/**
 * Set QR code URL for an event offer
 */
export async function setEventOfferQRCode(
  eventOfferId: UUID,
  qrCodeUrl: string,
  generatedUrl?: string,
): Promise<EventOffer> {
  const { data, error } = await supabase
    .from("event_offers")
    .update({
      qr_code_url: qrCodeUrl,
      ...(generatedUrl && { generated_url: generatedUrl }),
      updated_at: new Date().toISOString(),
    })
    .eq("id", eventOfferId)
    .select(
      `
      *,
      offer:offers(*)
    `,
    )
    .single();

  if (error) {
    console.error("Error setting QR code URL:", error);
    throw error;
  }

  return data;
}

// =====================================================
// OFFER SEARCH/LOOKUP
// =====================================================

/**
 * Get all available offers (for attaching to events)
 */
export async function getAvailableOffers(): Promise<
  Array<{
    id: UUID;
    offer_code: string;
    name: string;
    description?: string;
    offer_type: string;
    value_type: string;
    value_amount?: number;
    is_active: boolean;
  }>
> {
  const { data, error } = await supabase
    .from("offers")
    .select(
      "id, offer_code, name, description, offer_type, value_type, value_amount, is_active",
    )
    .eq("is_active", true)
    .or("status.is.null,status.eq.active")
    .order("name");

  if (error) {
    console.error("Error fetching available offers:", error);
    throw error;
  }

  return data || [];
}

/**
 * Set (or clear) the SendGrid email template used to deliver an offer's voucher
 * — including the Event Offer Reward email. Lives on the `offers` row, so it
 * applies to every event that links this offer. Pass null to clear it (voucher
 * still mints, but no email is sent).
 */
export async function updateOfferEmailTemplate(
  offerId: UUID,
  emailTemplateId: string | null,
): Promise<void> {
  const { error } = await supabase
    .from("offers")
    .update({ email_template_id: emailTemplateId })
    .eq("id", offerId);

  if (error) {
    console.error("Error updating offer email template:", error);
    throw error;
  }
}

/**
 * Send a batch reminder that re-emails the EXISTING reward vouchers (same code +
 * QR) to everyone who already received one for this event. Re-send only — never
 * mints new vouchers. Each call is a fresh batch (new batch_key), so it can be
 * sent multiple times; suppression is honored server-side.
 */
export async function sendEventRewardReminder(params: {
  eventId: UUID;
  eventOfferId?: UUID;
  templateId?: string;
  includeNonActive?: boolean;
}): Promise<{
  batch_id: string;
  total: number;
  sent: number;
  failed: number;
  skipped: number;
  already_completed?: boolean;
}> {
  const { data: session } = await supabase.auth.getSession();
  if (!session?.session) {
    throw new Error("No active session");
  }
  const batch_key = crypto.randomUUID();
  const resp = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-event-reward-reminder`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${session.session.access_token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        event_id: params.eventId,
        event_offer_id: params.eventOfferId,
        template_id: params.templateId,
        include_non_active: params.includeNonActive,
        batch_key,
      }),
    },
  );
  const dataJson = await resp.json().catch(() => ({}));
  if (!resp.ok || !dataJson.success) {
    throw new Error(dataJson.error || `Reminder send failed (${resp.status})`);
  }
  return dataJson;
}

/**
 * Search offers by name or code
 */
export async function searchOffers(query: string): Promise<
  Array<{
    id: UUID;
    offer_code: string;
    name: string;
    description?: string;
    offer_type: string;
    value_type: string;
    value_amount?: number;
  }>
> {
  const { data, error } = await supabase
    .from("offers")
    .select(
      "id, offer_code, name, description, offer_type, value_type, value_amount",
    )
    .eq("is_active", true)
    .or(`name.ilike.*${query}*,offer_code.ilike.*${query}*`)
    .order("name")
    .limit(20);

  if (error) {
    console.error("Error searching offers:", error);
    throw error;
  }

  return data || [];
}
